import { getPreviewTag } from '@clofo/data/lib'

/**
 * The dev-only logged-in placeholder page (see pages/dev-logged-in.tsx), used as the post-login
 * landing page in local dev where no destination app is served.
 */
export const DEV_LOGGED_IN_URL = '/#/dev-logged-in'

/**
 * The path (with search and hash) of a same-origin http(s) destination, or null for anything else.
 * `next` comes from the URL, so a crafted login link could otherwise send the user off-site or run
 * a `javascript:` URL. Returning the re-serialized path rather than the input means what we
 * navigate to is exactly what was checked.
 */
const toSameOriginPath = (destination: string) => {
  let url: URL
  try {
    url = new URL(destination, window.location.origin)
  } catch {
    return null
  }
  const isHttp = url.protocol === 'http:' || url.protocol === 'https:'
  if (!isHttp || url.origin !== window.location.origin) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Decoded `next` param carried in the login URL, or null when absent or unsafe. Mirrors the legacy
 * login app: `next` is a base64-encoded path (set by `http.redirectToLoginWithNextUrl()` as
 * `/apps/login#/?next=<base64>`).
 */
const getNextDestination = () => {
  const raw = window.location.href.match(/next=([^&#]*)/)?.[1]
  if (!raw) return null
  let decoded: string
  try {
    decoded = window.atob(raw)
  } catch {
    // Malformed/un-decodable next — fall through to the default destination.
    console.warn('Ignoring malformed `next` redirect param:', raw)
    return null
  }
  const path = toSameOriginPath(decoded)
  if (!path) {
    console.warn('Ignoring off-site `next` redirect param:', decoded)
  }
  return path
}

/**
 * Default destination when the login URL carries no `next` — the overview app. When this build is
 * itself a PR preview, the same preview of overview, matching the links posted on the PR, so a
 * preview login session stays inside the preview.
 */
const getDefaultDestination = () => {
  const previewTag = getPreviewTag()
  if (previewTag) {
    return `/react/${previewTag}/overview/index.html`
  }
  return '/apps/overview'
}

/**
 * Where to navigate after a successful login. Same as production, except in local dev with no
 * explicit `next`: no destination app runs on the login dev server (its SPA fallback would bounce
 * the user straight back to this login page), so we land on the logged-in placeholder instead.
 */
export const getRedirectTo = () => {
  const next = getNextDestination()
  if (next) return next
  if (import.meta.env.DEV) return DEV_LOGGED_IN_URL
  return getDefaultDestination()
}
